import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAttendanceByBatchId } from 'thunks/attendenceSliceThunks/thunks';
import Layout from 'Component/Layouts/layout';
import ViewAttendenceStats from 'Component/MultiStepTrainingForms/TrainingForm/ViewAttendenceStats';

export default function ViewStats() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const batchId = useMemo(() => queryParams.get('batchId'), [location]);
  const dispatch = useDispatch();

  const { data: attendenceData } = useSelector((state) => state.attendenceSlice);

  const handleBackToAttendance = () => {
    navigate(`/attendance?batchId=${batchId}`);
  };

  useEffect(() => {
    if (batchId) {
      dispatch(fetchAttendanceByBatchId(batchId));
    }
  }, [batchId, dispatch]);

  // Refresh data when attendance data changes
  useEffect(() => {
    if (attendenceData?.status === 'success') {
      // Data is already loaded, no need to fetch again
      console.log('Attendance data loaded for stats:', attendenceData);
    }
  }, [attendenceData?.status]);

  return (
    <Layout>
      <Box sx={{ padding: '20px' }}>
        {/* Header with Back Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
            gap: 2,
          }}
        >
          <IconButton
            onClick={handleBackToAttendance}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Attendance Statistics - Batch {batchId}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Stats Overview Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Total Trainees" />
              <CardContent>
                <Typography variant="h4" color="primary">
                  {attendenceData?.data?.batchTrainees?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Total Days" />
              <CardContent>
                <Typography variant="h4" color="primary">
                  {attendenceData?.data?.attendance?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Average Attendance" />
              <CardContent>
                <Typography variant="h4" color="primary">
                  {/* Calculate average attendance percentage */}
                  {attendenceData?.data?.attendance?.length > 0
                    ? Math.round(
                        (attendenceData.data.attendance.reduce(
                          (acc, day) => acc + (day.present?.length || 0),
                          0
                        ) /
                          (attendenceData.data.batchTrainees?.length *
                            attendenceData.data.attendance.length)) *
                          100
                      )
                    : 0}
                  %
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Detailed Stats Component */}
          <Grid item xs={12}>
            <Paper sx={{ padding: '20px' }}>
              <Typography variant="h6" gutterBottom>
                Detailed Statistics
              </Typography>
              <ViewAttendenceStats />
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Box
                variant="outlined"
                onClick={handleBackToAttendance}
                sx={{
                  cursor: 'pointer',
                  padding: '10px 20px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Back to Attendance
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
